import { useCallback } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { UploadOutlined } from '@ant-design/icons';

import { useFileSelector } from '@/hooks/useFileSelector';
import { useUploader } from '@/hooks/useUploader';
import { cn } from '@/lib/cn';

import { UploadItem } from './uploadItem';
import { ErrorMessage } from '../ErrorMessage';

export const Uploader = ({ name }: { name: string }) => {
  const { control } = useFormContext();
  const { append, remove, fields } = useFieldArray({
    control,
    name
  });

  const { selectDeliverables } = useFileSelector();
  const { addFile, uploads, done, abort, retry, isFull } = useUploader({
    onUploadComplete: ({ hash, id, filename, deliverableId, size }) => {
      append({
        hash,
        filename,
        deliverableId,
        id,
        size
      });
    },
    onUploadDelete: (id, filename) => {
      const index = fields.findIndex((f: any) => f.deliverableId === id || f.id === id || f.filename === filename);

      if (index !== -1) {
        remove(index);
      }
    }
  });

  const handleClick = useCallback(async () => {
    try {
      if (isFull) {
        return;
      }

      const files = await selectDeliverables();

      files.forEach(addFile);
    } catch (error) {
      console.error('File selection was cancelled or failed:', error);
    }
  }, [selectDeliverables, addFile, isFull]);

  return (
    <div>
      {!!uploads.length && (
        <div>
          {uploads.map(u => (
            <UploadItem key={u.id} upload={u} done={done} abort={abort} retry={retry} />
          ))}
        </div>
      )}
      <div
        onClick={handleClick}
        id='upload-files-button'
        className={cn(
          'bg-surface-default border-outline-default flex cursor-pointer flex-row items-center justify-between gap-4 rounded-lg border border-dashed p-4 [stroke-dasharray:4,4]',
          { 'cursor-not-allowed': isFull }
        )}
      >
        <div className='dark:bg-secondary-50/10 flex h-8 w-8 items-center justify-center rounded-full bg-sky-100'>
          <UploadOutlined className='text-primary' />
        </div>
        <div className='flex-1'>
          <div className='text-md text-primary font-semibold'>
            <span className='hidden md:inline'>Clique </span>
            <span className='inline md:hidden'>Toque </span>
            para enviar os arquivos
          </div>
          <div className='text-content-caption text-xs'>PDF, EPUB, MOBI, DOCX, JPG, PNG, CSV</div>
        </div>
      </div>
      <ErrorMessage fieldName={name} />
    </div>
  );
};
